import { Pagination, PaginationRequest } from '../../clients/quipli-service/pagination/pagination';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../util/toasts';
import { Spinner } from '../spinner/spinner';
import { debounce } from 'lodash';
import Icon from '../icons/icon';

export type ScrollableListParams<T> = {
  pageSize?: number;
  fetch: (pagination: PaginationRequest) => Promise<Pagination<T>>;
  onSelect: (value: T) => void;
  value?: T;
  itemKey: (value: T) => any;
  itemLabel: (value: T) => ReactNode;
};

export function ScrollableList<T>(params: ScrollableListParams<T>) {
  const { fetch, onSelect, value, itemLabel, itemKey } = params;
  const pageSize = params.pageSize || 25;
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [items, setItems] = useState<T[]>([]);
  const [response, setResponse] = useState<Pagination<T> | undefined>();
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const ref = useRef<HTMLOListElement>(null);

  useEffect(() => {
    setPageNumber(1);
  }, [fetch]);

  const debouncedFetch = useCallback(
    debounce(() => {
      setIsFetchingMore(true);
      fetch({
        pageNumber,
        pageSize,
      })
        .then((response) => {
          if (response.pagination.pageNumber === 1) {
            setItems(response.items);
          } else {
            setItems((prev) => [...prev, ...response.items]);
          }
          setResponse(response);
        })
        .catch((e) => {
          toast('Something went wrong', 'error');
          console.error(e);
        })
        .finally(() => setIsFetchingMore(false));
    }, 300),
    [fetch, pageSize, pageNumber],
  );

  useEffect(() => {
    debouncedFetch();
  }, [debouncedFetch]);

  function onScroll() {
    if (ref.current && !isFetchingMore) {
      const { scrollTop, scrollHeight, clientHeight } = ref.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight;
      if (isNearBottom && (!response || pageNumber < response.pagination.lastPage)) {
        setPageNumber((prev) => prev + 1);
      }
    }
  }

  return (
    <ol ref={ref} className="overflow-y-auto" onScroll={onScroll}>
      {items.map((item, index) => {
        return (
          <li
            key={index}
            onClick={() => {
              onSelect(item);
            }}
          >
            {itemLabel(item)}
            {value && itemKey(item) === itemKey(value) ? <Icon icon="Check" /> : undefined}
          </li>
        );
      })}
      {isFetchingMore && (
        <li>
          <Spinner />
        </li>
      )}
    </ol>
  );
}
